import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dots = ({ used, total = 6 }) => (
  <span className="font-mono text-xs">
    {Array.from({ length: total }).map((_, i) => (
      <span key={i} className={i < used ? 'text-emerald-600' : 'text-slate-300'}>● </span>
    ))}
  </span>
);

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

/**
 * VendorClaimsWidget
 *  - When userId is passed → shows that user's vendor/client onboarding claims.
 *  - When omitted → admin mode: shows ALL active claims across the org.
 */
const VendorClaimsWidget = ({ userId, title }) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const url = userId
      ? `${API_URL}/api/vendor-claims?user_id=${userId}`
      : `${API_URL}/api/vendor-claims`;
    axios.get(url, { withCredentials: true })
      .then(r => { if (alive) setClaims(r.data || []); })
      .catch(() => { if (alive) setClaims([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId]);

  const totalEarned = claims.reduce((s, c) => s + (c.earned || 0), 0);

  return (
    <Card className="border-slate-200" data-testid="vendor-claims-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center justify-between">
          <span>{title || 'Vendor & Client Onboarding Bonuses'}</span>
          {claims.length > 0 && (
            <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
              {claims.length} active · {fmt(totalEarned)} earned
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : claims.length === 0 ? (
          <p className="text-xs text-slate-400" data-testid="no-claims">
            {userId ? 'No new vendors or clients introduced yet.' : 'No active onboarding claims.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-2">Vendor / Client</th>
                  {!userId && <th className="text-left py-2 pr-2">Owner</th>}
                  <th className="text-left py-2 pr-2">Type</th>
                  <th className="text-left py-2 pr-2">Progress</th>
                  <th className="text-right py-2 pr-2">Earned</th>
                  <th className="text-right py-2">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((c, i) => {
                  const totalCap = c.effective_type === 'new_client' ? 100000
                    : c.effective_type === 'existing_new' ? 20000
                    : 47500;
                  const remaining = Math.max(totalCap - (c.earned || 0), 0);
                  const typeLabel = c.kind === 'client' ? 'New Client'
                    : c.effective_type === 'existing_new' ? 'Existing New Vendor'
                    : 'New Vendor';
                  return (
                    <tr key={i} className="border-b border-slate-100" data-testid={`claim-row-${i}`}>
                      <td className="py-2 pr-2 text-slate-800 font-medium">{c.name}</td>
                      {!userId && <td className="py-2 pr-2 text-slate-600">{c.owner_name || '—'}</td>}
                      <td className="py-2 pr-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${c.kind === 'client' ? 'bg-blue-50 text-blue-700' : c.effective_type === 'existing_new' ? 'bg-cyan-50 text-cyan-700' : 'bg-emerald-50 text-emerald-700'}`}>{typeLabel}</span>
                      </td>
                      <td className="py-2 pr-2">
                        <Dots used={c.stages_used} total={c.stages_total} />
                        <span className="ml-2 text-slate-500">{c.stages_used}/{c.stages_total}</span>
                      </td>
                      <td className="py-2 pr-2 text-right font-semibold text-emerald-700">{fmt(c.earned)}</td>
                      <td className="py-2 text-right text-slate-500">{fmt(remaining)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VendorClaimsWidget;
